import { ExternalLink, LayoutGrid, Lock, Mail } from "lucide-react";

import {
  type PortalCatalogEntry,
  portalLaunchUrl,
  resolveIconSrc,
  useAppsCatalog,
} from "@/api/apps";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { resolveTheme, useThemeStore } from "@/stores/theme";

/**
 * Apps launcher — the M9.5 entry point for the ATTMOS Suite
 * Unification surface (epic #340, Phase A).
 *
 * Phase A scope is launcher-only: clicking a card opens the portal
 * in a new tab via ``window.open(url, '_blank', 'noopener,noreferrer')``.
 * No iframe; no cross-origin CSP coordination needed. Iframe embedding
 * is deferred to P1.0+ per the launcher-only pivot.
 *
 * Entitlement is server-side — ``/api/apps`` only returns the portals
 * the session's Cognito groups grant access to (anchor §5). Whatever
 * shows up here, the user is entitled to launch.
 */
export function AppsLauncherPage(): JSX.Element {
  const { data: catalog, isLoading, isError, error } = useAppsCatalog();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="ATTMOS Suite" description="Loading…" />
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="ATTMOS Suite" />
        <EmptyState
          title="Could not load Apps."
          description={error instanceof Error ? error.message : "Unknown error."}
        />
      </div>
    );
  }

  if (!catalog || catalog.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="ATTMOS Suite" />
        <EmptyState
          icon={<LayoutGrid className="h-8 w-8 text-muted-foreground" aria-hidden />}
          title="No ATTMOS apps available to you."
          description="Contact a FlowStack admin if you expected to see apps here."
        />
      </div>
    );
  }

  const entitledCount = catalog.filter((p) => p.entitled).length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="ATTMOS Suite"
        description={
          entitledCount === catalog.length
            ? `${catalog.length} apps entitled to your account. Click any tile to open in a new tab.`
            : `${entitledCount} of ${catalog.length} apps entitled. Locked tiles open a request-access email to the owning team.`
        }
      />
      <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {catalog.map((portal) => (
          <PortalTile key={portal.slug} portal={portal} />
        ))}
      </div>
    </div>
  );
}

/**
 * Logo-prominent launcher tile. Square-ish aspect; logo panel hosts the
 * portal's brand mark (60-70% of the card surface); display_name +
 * hostname stack below. Matches the ATTMOS Suite dashboard mockup.
 *
 * Two states branched on ``portal.entitled``:
 *
 * - **Entitled** — anchor opens portal in a new tab via
 *   ``window.open(url, '_blank', 'noopener,noreferrer')``-equivalent
 *   markup. Hover lifts the card; cyan ExternalLink icon in footer.
 * - **Not entitled** — anchor opens a request-access mailto pointing
 *   at the portal's ``owner_contact.email`` with a pre-filled
 *   subject. Logo is desaturated + dimmed; Lock badge in the upper-
 *   right; "Request access" + Mail icon in footer.
 *
 * Showing all listed portals (entitled + not-entitled-but-listed)
 * keeps the dashboard substantial even for users entitled to one
 * app, and gives non-entitled users a clear next action.
 * ``listed_in_catalog: False`` portals are hidden server-side per
 * anchor §4.1; they never reach this component.
 *
 * ``noopener,noreferrer`` on the entitled branch strips the
 * back-reference so the opened portal can't manipulate the
 * launcher (window.opener tampering).
 */
function PortalTile({ portal }: { portal: PortalCatalogEntry }): JSX.Element | null {
  // Theme-aware logo selection — mirrors the FlowStack Header's
  // resolveTheme(useThemeStore()) pattern. The image-src derivation
  // is delegated to ``resolveIconSrc`` (pure helper in
  // ``@/api/apps``) so it can be unit-tested deterministically. Per
  // Rupert PR-360 r0 #2.
  const theme = useThemeStore((s) => s.theme);
  const resolvedTheme = resolveTheme(theme);
  const iconSrc = resolveIconSrc(portal.icon, resolvedTheme);

  if (portal.entitled) {
    const url = portalLaunchUrl(portal);
    // Same defense-in-depth gate as Phase E: a forward-compat / server
    // bug that lets an off-domain manifest through must not render an
    // unsafe link. Skip the tile rather than render a broken anchor.
    if (url === null) return null;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "group block rounded-2xl outline-none",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        )}
        aria-label={`Open ${portal.display_name} in a new tab`}
      >
        <TileChrome
          iconSrc={iconSrc}
          iconAlt={portal.icon.alt}
          displayName={portal.display_name}
          hostname={portal.embed.hostname}
          entitled
        />
      </a>
    );
  }

  // Not entitled — request-access mailto. Subject is properly
  // URL-encoded; email is interpolated raw because the backend
  // ``PortalManifest.owner_contact.email`` is validated as
  // ``EmailStr`` and can't contain mailto-breaking characters.
  // (Same reasoning + comment as Phase E's EntitlementPanel.)
  const subject = encodeURIComponent(`FlowStack: request access to ${portal.display_name}`);
  return (
    <a
      href={`mailto:${portal.owner_contact.email}?subject=${subject}`}
      className={cn(
        "group block rounded-lg outline-none",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      )}
      aria-label={`Email ${portal.owner_contact.team} to request access to ${portal.display_name}`}
    >
      <TileChrome
        iconSrc={iconSrc}
        iconAlt={portal.icon.alt}
        displayName={portal.display_name}
        hostname={portal.embed.hostname}
        entitled={false}
      />
    </a>
  );
}

interface TileChromeProps {
  iconSrc: string;
  iconAlt: string;
  displayName: string;
  hostname: string;
  entitled: boolean;
}

function TileChrome({
  iconSrc,
  iconAlt,
  displayName,
  hostname,
  entitled,
}: TileChromeProps): JSX.Element {
  return (
    <Card
      elevation="raised"
      className={cn(
        "relative flex h-full flex-col overflow-hidden rounded-2xl p-0",
        "transition-all duration-200",
        // The entitled hover read `shadow-lg shadow-cyan/10`. A
        // `shadow-<colour>` utility cannot survive elevation moving into CSS
        // custom properties (U1.2): Tailwind only rewrites a shadow's colour
        // when it can parse one out of the value, and `var(--elevation-lg)`
        // has none, so the modifier would have become a silent no-op.
        // `--elevation-accent` is the same lift with the cyan already in it,
        // and unlike the utility it has a dark-theme value of its own.
        entitled
          ? "group-hover:-translate-y-0.5 group-hover:border-cyan/40 group-hover:shadow-[var(--elevation-accent)]"
          : "group-hover:border-border group-hover:shadow"
      )}
    >
      {/* Locked badge — small lock chip in the corner, visible only
          on non-entitled tiles. */}
      {!entitled ? (
        <span
          className={cn(
            "absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full",
            "border border-border-subtle bg-card/90 px-2 py-0.5 text-[10px] font-medium",
            "text-muted-foreground backdrop-blur-sm"
          )}
        >
          <Lock className="h-3 w-3" aria-hidden />
          Locked
        </span>
      ) : null}
      {/* Logo panel — blends with current FlowStack theme via
          ``bg-card``; the theme-paired ``logo-light.png`` /
          ``logo-dark.png`` swap above keeps brand legibility on
          both light + dark chrome. Desaturate + dim on locked. */}
      <div
        className={cn(
          "flex aspect-[5/4] items-center justify-center bg-card p-6",
          !entitled && "opacity-50 saturate-0 transition-all group-hover:opacity-70"
        )}
      >
        <img
          src={iconSrc}
          alt={iconAlt}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
        />
      </div>
      {/* Footer strip — name + hostname (entitled) OR name + Request
          access (locked). */}
      <div className="flex flex-1 flex-col gap-0.5 border-t border-border-subtle p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display text-sm font-semibold leading-tight">{displayName}</p>
          {entitled ? (
            <ExternalLink
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors",
                "group-hover:text-cyan"
              )}
              aria-hidden
            />
          ) : (
            <Mail
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors",
                "group-hover:text-foreground"
              )}
              aria-hidden
            />
          )}
        </div>
        <p className="truncate font-mono text-[10px] text-muted-foreground">
          {entitled ? hostname : "Request access"}
        </p>
      </div>
    </Card>
  );
}
